/**
 * Opening Touch detail already narrowed to a person or a team — the Ledger's
 * "Touches" buttons in the person drawer, the Me hero, the supervisor drawer and the
 * Overview slice.
 *
 * The selection travels in router state, not the URL: a URL carrying staff names
 * ends up in browser history, proxy logs and pasted links.
 *
 *   const openTouches = useOpenTouchDetail()
 *   openTouches({ employees: [p.employee], label: p.employee, basis: 'claim' })
 *   openTouches({ employees: team.map((p) => p.employee), label: `team of ${sup}`, basis: 'claim' })
 *   openTouches({ employees: slice, label: facility, pick: { f: facility } })
 *   openTouches({ employees: [], label: `visit ${v}`, claim: { visit: v, payer } })
 *
 * One person selects them in the Employee dropdown, as hers does; more than one
 * becomes a removable "team of …" tag on the count line.
 */
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TouchBasis } from './touchRows'

export interface TouchDrill {
  employees: string[]
  /** What the tag on the count line says, e.g. "team of Jane Doe". */
  label: string
  /**
   * An Overview slice the touch rows carry — facility group, facility, state or payer
   * type, matched blank-for-blank (data/blankValue.ts) — set in Touch detail's own
   * dropdown rather than as people. If the value is not in the rows, `employees` is
   * used after all.
   */
  pick?: Partial<Record<TouchPickKey, string>>
  /**
   * 'claim' opens on the touches on these people's resolved claims billed in the
   * month range — what the person page, the team drawer and the Touch profile count
   * (review #23). Without it, the touches they made, by touch date.
   */
  basis?: TouchBasis
  /**
   * Narrower than the top bar's range: the Touch profile's held month. A removable
   * tag on the count line, as a team is.
   */
  months?: string[]
  /**
   * One claim's touches, from Claim detail's Touches column (review #165): its visit
   * and payer, every date, whoever made them. Overrides the people and months.
   */
  claim?: { visit: string; payer: string }
  /**
   * Touch drivers' figures (KANB-2235, 2236): exactly the rows a count there counted.
   * The touches these people made in the top bar's months, narrowed to a trimmed status
   * and action — every spelling, trailing spaces or not — and to these claims (facility
   * + visit); with `repeatsOnly`, only the repeat touches, found with the same look-back
   * month the page used. A removable tag on the count line. Overrides the people.
   */
  drivers?: TouchDriversDrill
}

export interface TouchDriversDrill {
  people: string[]
  status?: string
  action?: string
  claims?: { f: string; v: string }[]
  repeatsOnly?: boolean
  lookBack: string[]
  /** What the tag says. */
  label: string
}

export type TouchPickKey = 'fg' | 'f' | 's' | 'p'

export function useOpenTouchDetail(): (drill: TouchDrill) => void {
  const navigate = useNavigate()
  return useCallback(
    (drill: TouchDrill) => navigate('/touch-detail', { state: { touchDrill: drill } }),
    [navigate],
  )
}

/** The drill a navigation carried, if any. */
export function readTouchDrill(state: unknown): TouchDrill | null {
  const d = (state as { touchDrill?: TouchDrill } | null)?.touchDrill
  return d && Array.isArray(d.employees) && typeof d.label === 'string' ? d : null
}
